/**
 * rsa-event-row block
 *
 * Horizontal event card row used on the RSA 2026 landing page.
 * Layout: illustration (left) · eyebrow/title/copy/tags/CTA (middle) · month-day calendar card (right)
 * Headings support <br/> so authors can match mockup line breaks exactly.
 *
 * The CTA renders as a plain link by default. Set `cta.popup: true` to open
 * `cta.url` in a centered popup window (with new-tab fallback when popups are
 * blocked). Useful for round-robin scheduling pages that want a focused
 * modal-like experience without an SDK.
 */
const POPUP_WIDTH = 720;
const POPUP_HEIGHT = 760;

function openPopup(url: string, name = "c1-popup") {
  if (typeof window === "undefined") return;
  const left = Math.max(0, (window.screen.width - POPUP_WIDTH) / 2);
  const top = Math.max(0, (window.screen.height - POPUP_HEIGHT) / 2);
  const features = `popup=yes,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable=yes,scrollbars=yes`;
  const win = window.open(url, name, features);
  // Some browsers (and most mobile browsers) block popups silently. Fall back
  // to a normal new-tab navigation so the user always reaches the scheduler.
  if (!win) {
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    win.focus();
  }
}
export function RsaEventRow({
  image,
  image_alt,
  eyebrow,
  heading,
  heading_font = "display",
  heading_size = "default",
  vertical_align = "center",
  margin_bottom = "none",
  mobile_calendar = "below-copy",
  copy,
  date_tag,
  location_tag,
  month,
  day,
  cta,
}: {
  image?: string;
  image_alt?: string;
  eyebrow?: string;
  heading?: string;
  /** Heading typeface. "display" (Anybody, default) or "body" (Public Sans). */
  heading_font?: "display" | "body";
  /** Heading size. "default" (25/28px) or "20" (fixed 20px). */
  heading_size?: "default" | "20";
  /**
   * Vertical anchoring of the image + calendar columns. "center" (default)
   * centers them in the row; "top" pins them to the top so the calendar
   * lines up with the top of the event image. The text column stays centered.
   */
  vertical_align?: "center" | "top";
  /** Extra space below the row. "none" (default) or "100" (100px). */
  margin_bottom?: "none" | "100";
  /**
   * Where the calendar stacks on mobile. "below-copy" (default) keeps the
   * source order (image → text → calendar); "above-copy" reorders it to
   * image → calendar → text so the calendar sits under the image and above
   * the copy. Desktop layout (image · text · calendar) is unchanged.
   */
  mobile_calendar?: "below-copy" | "above-copy";
  copy?: string;
  date_tag?: string;
  location_tag?: string;
  month?: string;
  day?: string;
  cta?: {
    text: string;
    url?: string;
    /** When true, open `url` in a centered popup window (falls back to a new tab). */
    popup?: boolean;
  };
}) {
  const ctaClassName =
    "inline-flex items-center gap-2 px-6 py-2.5 font-body font-medium leading-none rounded-md text-white bg-teal-cta hover:bg-teal-cta-hover active:bg-teal-cta-hover/90 text-sm";
  const ctaInner = cta ? (
    <>
      <span>{cta.text}</span>
      <span aria-hidden="true">→</span>
    </>
  ) : null;
  const pinTop = vertical_align === "top" ? "self-start" : "";
  const marginBottom = margin_bottom === "100" ? "mb-[100px]" : "";
  // Mobile-only reorder: image → calendar → text. Reset at lg back to the
  // source order so the desktop columns stay image · text · calendar.
  const reorder = mobile_calendar === "above-copy";
  const imageOrder = reorder ? "order-1" : "";
  const textOrder = reorder ? "order-3 lg:order-2" : "";
  const calendarOrder = reorder ? "order-2 lg:order-3" : "";
  return (
    <div className={`px-4 md:px-8 ${marginBottom}`}>
      <div className="max-w-screen-xl mx-auto">
        <div className="border-t border-gray-60 py-10 lg:py-14">
          <div className="grid gap-6 lg:gap-10 lg:grid-cols-12 items-center">
            {image && (
              <div className={`lg:col-span-4 ${imageOrder} ${pinTop}`}>
                <div className="rounded-2xl overflow-hidden bg-gray-100/40">
                  <img
                    src={image}
                    alt={image_alt || ""}
                    className="w-full"
                    loading="lazy"
                  />
                </div>
              </div>
            )}
            <div className={`lg:col-span-6 ${textOrder}`}>
              {eyebrow && (
                <p className="font-body text-gray-80 font-medium text-sm mb-1">
                  {eyebrow}
                </p>
              )}
              {heading && (
                <h3
                  className={`${
                    heading_font === "body" ? "font-body" : "font-display"
                  } text-charcoal font-bold ${
                    heading_size === "20" ? "text-[20px]" : "text-25 lg:text-28"
                  } leading-tight`}
                  dangerouslySetInnerHTML={{ __html: heading }}
                />
              )}
              {copy && (
                <p className="font-body text-gray-80 mt-3 leading-relaxed max-w-xl">
                  {copy}
                </p>
              )}
              {(date_tag || location_tag) && (
                <ul className="flex flex-wrap gap-2 mt-5">
                  {date_tag && (
                    <li className="font-medium text-sm text-teal-dark bg-teal-dark/10 rounded-full px-3 py-1 leading-tight">
                      {date_tag}
                    </li>
                  )}
                  {location_tag && (
                    <li className="font-medium text-sm text-teal-dark bg-teal-dark/10 rounded-full px-3 py-1 leading-tight">
                      {location_tag}
                    </li>
                  )}
                </ul>
              )}
              {cta && (
                <div className="mt-6">
                  <a
                    href={cta.url || "#"}
                    target={cta.url?.startsWith("http") ? "_blank" : undefined}
                    rel={cta.url?.startsWith("http") ? "noopener" : undefined}
                    onClick={
                      cta.popup && cta.url
                        ? (e) => {
                            e.preventDefault();
                            openPopup(cta.url!);
                          }
                        : undefined
                    }
                    className={ctaClassName}
                  >
                    {ctaInner}
                  </a>
                </div>
              )}
            </div>
            {(month || day) && (
              <div className={`lg:col-span-2 flex lg:justify-end ${calendarOrder} ${pinTop}`}>
                <div className="rounded-xl border border-gray-60 bg-white overflow-hidden w-24 text-center shadow-sm">
                  {month && (
                    <div className="bg-gray-100/60 px-3 py-1.5 font-body uppercase tracking-widest-2 text-xs font-semibold text-gray-80">
                      {month}
                    </div>
                  )}
                  {day && (
                    <div
                      className={`px-3 py-3 font-display font-bold text-charcoal leading-none whitespace-nowrap ${
                        day.length > 5
                          ? "text-lg"
                          : day.length > 2
                            ? "text-2xl"
                            : "text-4xl"
                      }`}
                    >
                      {day}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
